<template>
  <!-- Regular user: one card per review, grouped by location -->
  <template v-if="!isAdmin && assignmentsData">
    <template v-if="userLocationGroups.length > 0">
      <div v-for="group in userLocationGroups" :key="group.location" class="mb-6">
        <!-- Location section header -->
        <div class="text-subtitle-2 text-medium-emphasis mb-2 px-1">{{ group.location }}</div>
        <v-row>
          <template v-for="combo in group.assignments" :key="`${combo.platform}|${combo.brand}`">
            <!-- Empty pool / cooldown: one placeholder card per combo -->
            <v-col v-if="combo.poolEmpty || combo.reviews.length === 0" cols="12" sm="6">
              <v-card variant="outlined" class="fill-height" :class="{ 'review-copied': combo.poolEmpty }">
                <v-card-item>
                  <template v-slot:prepend>
                    <div :style="platformDotStyle(combo.platform)"></div>
                  </template>
                  <v-card-title class="text-body-1 font-weight-medium">{{ combo.platform }}</v-card-title>
                  <v-card-subtitle>{{ combo.brand }}</v-card-subtitle>
                </v-card-item>
                <v-divider />
                <v-card-text class="text-medium-emphasis" :class="{ 'font-italic': combo.poolEmpty }">
                  {{ combo.poolEmpty ? 'No reviews in pool for this combo' : 'All reviews on cooldown' }}
                </v-card-text>
              </v-card>
            </v-col>

            <!-- One card per review -->
            <v-col
              v-for="review in combo.reviews"
              :key="review.id"
              cols="12"
              sm="6"
            >
              <v-card
                variant="outlined"
                class="fill-height"
                :class="{ 'review-copied': isReviewCompleted(review) }"
              >
                <!-- Header: platform, brand, action buttons -->
                <v-card-item>
                  <template v-slot:prepend>
                    <div :style="platformDotStyle(combo.platform)"></div>
                  </template>
                  <v-card-title class="text-body-1 font-weight-medium">{{ combo.platform }}</v-card-title>
                  <v-card-subtitle>{{ combo.brand }}</v-card-subtitle>
                  <!-- Action buttons in card header -->
                  <template v-slot:append>
                    <div class="d-flex align-center ga-1">
                      <!-- Copy to clipboard (disabled when completed) -->
                      <v-btn icon size="small" variant="tonal" color="primary" :disabled="isReviewCompleted(review)" @click="copyReview(review)">
                        <v-icon v-if="copiedReviewId === review.id" color="success">mdi-check</v-icon>
                        <v-icon v-else>mdi-content-copy</v-icon>
                        <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                      </v-btn>
                      <!-- Upload screenshot (disabled when completed) -->
                      <v-btn
                        icon
                        size="small"
                        variant="tonal"
                        color="primary"
                        :disabled="isReviewCompleted(review)"
                        @click="openUpload(combo, review)"
                      >
                        <v-icon>mdi-camera</v-icon>
                        <v-tooltip activator="parent" location="top">Upload screenshot</v-tooltip>
                      </v-btn>
                    </div>
                  </template>
                </v-card-item>

                <v-divider />

                <!-- Review text body -->
                <v-card-text class="text-body-2" style="white-space: pre-line;">{{ review.text }}</v-card-text>

                <!-- Rating + reason + completed chip footer -->
                <v-card-actions class="px-4 pt-0">
                  <span class="text-caption">{{ review.rating }}</span>
                  <v-icon icon="mdi-star" color="orange" size="x-small" class="ml-1" />
                  <!-- Reason chip: only show Response (Baseline is the default/silent case) -->
                  <v-chip
                    v-if="review.reason === 'Response'"
                    size="x-small"
                    color="warning"
                    variant="tonal"
                    class="ml-2"
                  >Response</v-chip>
                  <v-chip
                    v-if="isReviewCompleted(review)"
                    size="x-small"
                    color="success"
                    variant="flat"
                    class="ml-2"
                  >
                    <v-icon start size="x-small">mdi-check-circle</v-icon>
                    Completed
                  </v-chip>
                </v-card-actions>
              </v-card>
            </v-col>
          </template>
        </v-row>
      </div>
    </template>

    <!-- Empty states for regular users -->
    <v-empty-state
      v-else-if="assignmentsData.progress.total === 0"
      class="text-center py-8"
      headline="No reviews assigned to you yet"
      text="Ask an admin to assign you one or more reviews to complete"
      icon="mdi-clipboard-alert-outline"
    />
    <v-empty-state
      v-else-if="assignmentsData.progress.remaining === 0"
      class="text-center py-8"
      :headline="`All ${assignmentsData.progress.total} reviews completed for ${clientDateFormatted}`"
      text="Come back tomorrow for fresh reviews"
      icon="mdi-check-circle-outline"
    />
  </template>

  <!-- Admin: one card per review, grouped by user+location -->
  <template v-else-if="isAdmin && adminData">
    <v-empty-state
      v-if="adminData.users.length === 0"
      class="text-center py-8"
      :headline="`No review assignments for ${clientDateFormatted}`"
      text="No assignments have been created for today"
      icon="mdi-clipboard-alert-outline"
    />

    <div
      v-for="block in adminLocationBlocks"
      :key="`${block.userId}|${block.location}`"
      class="mb-6"
    >
      <!-- User+location section header with progress chip -->
      <div class="d-flex align-center mb-2 px-1">
        <v-icon size="small" class="mr-2">mdi-account</v-icon>
        <span class="text-subtitle-2">{{ getUserName(block.userId, block.location) }}</span>
        <span class="text-caption text-medium-emphasis ml-2">{{ getUserEmail(block.userId) }}</span>
        <v-spacer />
        <v-chip
          size="small"
          :color="block.progress.remaining === 0 ? 'success' : 'warning'"
          variant="tonal"
        >
          {{ block.progress.copied }}/{{ block.progress.total }}
        </v-chip>
      </div>

      <v-row>
        <template v-for="combo in block.assignments" :key="`${combo.platform}|${combo.brand}`">
          <!-- Empty pool / cooldown placeholder -->
          <v-col v-if="combo.poolEmpty || combo.reviews.length === 0" cols="12" sm="6">
            <v-card variant="outlined" class="fill-height" :class="{ 'review-copied': combo.poolEmpty }">
              <v-card-item>
                <template v-slot:prepend>
                  <div :style="platformDotStyle(combo.platform)"></div>
                </template>
                <v-card-title class="text-body-1 font-weight-medium">{{ combo.platform }}</v-card-title>
                <v-card-subtitle>{{ combo.brand }}</v-card-subtitle>
                <!-- Admin dots menu for empty/cooldown combos (still editable/deletable) -->
                <template v-slot:append>
                  <v-menu>
                    <template v-slot:activator="{ props: menuProps }">
                      <v-btn icon size="small" variant="text" v-bind="menuProps">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list density="compact">
                      <v-list-item @click="openEditDialog(combo, block.userId, 'edit')">
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="openEditDialog(combo, block.userId, 'duplicate')">
                        <v-list-item-title>Duplicate</v-list-item-title>
                      </v-list-item>
                      <v-divider />
                      <v-list-item class="text-error" @click="deleteCombo(combo)">
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-card-item>
              <v-divider />
              <v-card-text class="text-medium-emphasis" :class="{ 'font-italic': combo.poolEmpty }">
                {{ combo.poolEmpty ? 'No reviews in pool for this combo' : 'All reviews on cooldown' }}
              </v-card-text>
            </v-card>
          </v-col>

          <!-- One card per review -->
          <v-col
            v-for="review in combo.reviews"
            :key="review.id"
            cols="12"
            sm="6"
          >
            <v-card
              variant="outlined"
              class="fill-height"
              :class="{ 'review-copied': isReviewCompleted(review) }"
            >
              <!-- Header: platform, brand, action buttons -->
              <v-card-item>
                <template v-slot:prepend>
                  <div :style="platformDotStyle(combo.platform)"></div>
                </template>
                <v-card-title class="text-body-1 font-weight-medium">{{ combo.platform }}</v-card-title>
                <v-card-subtitle>{{ combo.brand }}</v-card-subtitle>
                <!-- Action buttons in card header -->
                <template v-slot:append>
                  <div class="d-flex align-center ga-1">
                    <!-- Admin: copy button (disabled when completed) -->
                    <v-btn
                      icon
                      size="small"
                      variant="tonal"
                      color="primary"
                      :disabled="isReviewCompleted(review)"
                      @click="copyReview(review)"
                    >
                      <v-icon v-if="copiedReviewId === review.id" color="success">mdi-check</v-icon>
                      <v-icon v-else>mdi-content-copy</v-icon>
                      <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                    </v-btn>
                    <!-- Upload screenshot (disabled when completed) -->
                    <v-btn
                      icon
                      size="small"
                      variant="tonal"
                      color="primary"
                      :disabled="isReviewCompleted(review)"
                      @click="openUpload(combo, review)"
                    >
                      <v-icon>mdi-camera</v-icon>
                      <v-tooltip activator="parent" location="top">Upload screenshot</v-tooltip>
                    </v-btn>
                    <!-- Admin dots menu — only on first review card to avoid duplication across cards -->
                    <v-menu v-if="combo.reviews.indexOf(review) === 0">
                      <template v-slot:activator="{ props: menuProps }">
                        <v-btn icon size="small" variant="text" v-bind="menuProps">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list density="compact">
                        <!-- Edit: bulk-deletes all combo assignments then recreates with new settings -->
                        <v-list-item @click="openEditDialog(combo, block.userId, 'edit')">
                          <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <!-- Duplicate: creates new assignments pre-filled from this combo -->
                        <v-list-item @click="openEditDialog(combo, block.userId, 'duplicate')">
                          <v-list-item-title>Duplicate</v-list-item-title>
                        </v-list-item>
                        <v-divider />
                        <!-- Delete: bulk-deletes all assignment IDs for this combo -->
                        <v-list-item class="text-error" @click="deleteCombo(combo)">
                          <v-list-item-title>Delete</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </template>
              </v-card-item>

              <v-divider />

              <!-- Review text body -->
              <v-card-text class="text-body-2" style="white-space: pre-line;">{{ review.text }}</v-card-text>

              <!-- Rating + reason + completed chip footer -->
              <v-card-actions class="px-4 pt-0">
                <span class="text-caption">{{ review.rating }}</span>
                <v-icon icon="mdi-star" color="orange" size="x-small" class="ml-1" />
                <!-- Only show Response chip (Baseline is the silent default) -->
                <v-chip
                  v-if="review.reason === 'Response'"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                  class="ml-2"
                >Response</v-chip>
                <v-chip
                  v-if="isReviewCompleted(review)"
                  size="x-small"
                  color="success"
                  variant="flat"
                  class="ml-2"
                >
                  <v-icon start size="x-small">mdi-check-circle</v-icon>
                  Completed
                </v-chip>
              </v-card-actions>
            </v-card>
          </v-col>
        </template>
      </v-row>
    </div>
  </template>
</template>

<script setup>
/**
 * Card/grid view for daily review assignments.
 * One card per review with action buttons (copy, upload) in the card header.
 * Grouped by location (chef) or user+location (admin).
 */
import { useReviewsDailyState } from "@/composables/useReviewsDailyState.js";

const {
  isAdmin,
  assignmentsData,
  adminData,
  copiedReviewId,
  userLocationGroups,
  adminLocationBlocks,
  getPlatformGradient,
  getUserName,
  getUserEmail,
  isReviewCompleted,
  copyReview,
  openUpload,
  openEditDialog,
  deleteCombo,
  clientDateFormatted,
} = useReviewsDailyState();

/** Inline style for the colored platform dot */
const platformDotStyle = (platform) => ({
  background: getPlatformGradient(platform),
  width: "20px",
  height: "20px",
  borderRadius: "4px",
  flexShrink: 0,
});
</script>

<style>
.review-copied {
  opacity: 0.4;
}
</style>
